import { getApiOrigin } from "../../config/apiBase";
import { request } from "../../utils/request";


/**
 * 根据任务ID获取说话人列表
 * 接口：GET /video/subtitles/speakers
 * @param taskId 任务ID
 * @returns Promise<Speakers.GetSpeakersByTaskResponse>
 */
export const getSpeakersByTaskApi = async (taskId?: number, seriesId?: number): Promise<Speakers.GetSpeakersByTaskResponse> => {
  try {
    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        speakerName: string;
        isSeriesSpeaker: boolean;
        description: string | null;
        seriesId: number;
        speakCount?: number;
        speak_count?: number;
        subtitleRespVOS: Array<{
          id: number;
          originText: string;
          score: number;
          originAudio: string;
          originStartTime: string;
          originEndTime: string;
        }> | null;
      }> | null;
      msg: string;
    }>(`/video/subtitles/speakers`, {
      method: 'GET',
      params: {
        ...(taskId != null ? { taskId } : {}),
        ...(seriesId != null ? { seriesId } : {}),
      }
    });

    if (response.code !== 0 || !response.data) {
      return {
        success: true,
        message: response.msg || '获取说话人列表成功（无数据）',
        data: []
      };
    }

    // 将新接口返回的数据映射为 Speakers.Speaker[]
    const speakers: Speakers.Speaker[] = response.data.map(item => ({
      id: item.id,
      series_id: item.seriesId,
      task_id: taskId || 0,
      speaker_name: item.speakerName,
      description: item.description || '',
      is_series_speaker: item.isSeriesSpeaker || false,
      speak_count: item.speakCount ?? item.speak_count ?? 0,
      subtitles: (item.subtitleRespVOS || []).map(sub => ({
        id: sub.id,
        task_id: taskId || 0,
        origin_text: sub.originText,
        post_text: '',
        post_start_time: sub.originStartTime,
        post_end_time: sub.originEndTime,
        origin_start_time: sub.originStartTime,
        origin_end_time: sub.originEndTime,
        is_episode_voice: false,
        origin_audio: sub.originAudio,
        trans_audio: '',
        subtitle_id: sub.id,
        score: sub.score,
        audio_speed: 1.0,
        volume_gain_db: 1.0
      }))
    }));

    // 返回说话人数据

    return {
      success: true,
      message: '获取说话人列表成功',
      data: speakers
    };
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/video/subtitles/speakers',
      '任务ID': taskId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/video/subtitles/speakers?taskId=${taskId}`,
      '请求方法': 'GET'
    };

    console.error('=== 获取说话人列表API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);

    throw error;
  }
};

/**
 * 根据剧集ID获取所有说话人列表
 * 接口：GET /video/subtitles/speakers/all-by-series
 * @param seriesId 剧集ID
 * @returns Promise<Speakers.GetSpeakersByTaskResponse>
 */
export const getSpeakersBySeriesApi = async (seriesId: number): Promise<Speakers.GetSpeakersByTaskResponse> => {
  try {
    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        speakerName: string;
        isSeriesSpeaker: boolean;
        speakCount: number | null;
        voiceSourceType?: string | null;
        voiceToneId?: number | null;
        voiceToneName?: string | null;
        description: string | null;
        seriesId: number;
        langCode?: string;
        subtitleRespVOS: Array<{
          id: number;
          originText: string;
          score: number;
          originAudio: string;
          originStartTime: string;
          originEndTime: string;
        }> | null;
      }> | null;
      msg: string;
    }>(`/video/subtitles/speakers/all-by-series`, {
      method: 'GET',
      params: { seriesId }
    });

    if (response.code !== 0 || !response.data) {
      return {
        success: true,
        message: response.msg || '获取说话人列表成功（无数据）',
        data: []
      };
    }

    const speakers: Speakers.Speaker[] = response.data.map(item => ({
      id: item.id,
      series_id: item.seriesId,
      task_id: 0,
      speaker_name: item.speakerName,
      description: item.description || '',
      is_series_speaker: item.isSeriesSpeaker || false,
      speak_count: item.speakCount ?? null,
      voice_source_type: item.voiceSourceType ?? null,
      voice_tone_id: item.voiceToneId ?? null,
      voice_tone_name: item.voiceToneName ?? null,
      subtitles: (item.subtitleRespVOS || []).map(sub => ({
        id: sub.id,
        task_id: 0,
        origin_text: sub.originText,
        post_text: '',
        post_start_time: sub.originStartTime,
        post_end_time: sub.originEndTime,
        origin_start_time: sub.originStartTime,
        origin_end_time: sub.originEndTime,
        is_episode_voice: false,
        origin_audio: sub.originAudio,
        trans_audio: '',
        subtitle_id: sub.id,
        score: sub.score,
        audio_speed: 1.0,
        volume_gain_db: 1.0
      }))
    }));

    return {
      success: true,
      message: '获取说话人列表成功',
      data: speakers
    };
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': '/video/subtitles/speakers/all-by-series',
      '剧集ID': seriesId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求方法': 'GET'
    };

    console.error('=== 获取剧集说话人列表API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);

    throw error;
  }
};

/**
 * 新增说话人（视频编辑页）
 * 接口：POST /video/subtitles/add-speaker
 * 请求参数：
 * {
 *   "taskId": 1,
 *   "speakerName": "张三"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 * @param taskId 任务ID
 * @param speakerName 说话人名称
 * @returns Promise<{ code: number; data: boolean; msg: string }>
 */
export const checkAllGlobalSpeakersApi = async (
  params: Speakers.CheckAllGlobalParams
): Promise<Speakers.CheckAllGlobalResponse> => {
  const query = new URLSearchParams();
  query.set("seriesId", String(params.seriesId));
  params.taskIds.forEach((taskId) => {
    query.append("taskIds", String(taskId));
  });

  return request<Speakers.CheckAllGlobalResponse>(
    `/video/subtitles/speakers/check-all-global?${query.toString()}`,
    {
      method: "GET",
    }
  );
};

export const checkAllSpeakersGlobalApi = async (
  seriesId: number,
  taskId: number
): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await checkAllGlobalSpeakersApi({
    seriesId,
    taskIds: [taskId],
  });

  return {
    code: response.code,
    data: !!response.data?.allGlobal,
    msg: response.data?.message || response.msg,
  };
};

export const addVideoSpeakerApi = async (
  taskId: number,
  speakerName: string
): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/add-speaker`,
      {
        method: "POST",
        body: { taskId, speakerName }
      }
    );
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/subtitles/add-speaker`,
      '任务ID': taskId,
      '说话人名称': speakerName,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/video/subtitles/add-speaker`,
      '请求方法': 'POST'
    };
    
    console.error('=== 新增说话人API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 创建说话人（剧集音色库新增角色）
 * 接口：POST /video/voice-tone/add-series-speaker
 * @param params 创建说话人参数 { seriesId, speakerName, langCode }
 * @returns Promise<Speakers.CreateSpeakerResponse>
 */
export const createSpeakerApi = async (params: Speakers.CreateSpeakerParams): Promise<Speakers.CreateSpeakerResponse> => {
  try {
    const response = await request<{
      code: number;
      data: {
        id: number;
        speakerName: string;
        isSeriesSpeaker: boolean;
        description: string | null;
        seriesId: number;
        subtitleRespVOS: any[] | null;
      };
      msg: string;
    }>(
      `/video/voice-tone/add-series-speaker`,
      {
        method: "POST",
        body: {
          seriesId: params.seriesId,
          speakerName: params.speakerName,
          langCode: params.langCode
        }
      }
    );
    
    // 处理新接口响应格式 { code, data, msg }
    if (response.code === 0 && response.data) {
      // 将新接口返回的数据转换为 Speakers.Speaker 格式
      const speakerData: Speakers.Speaker = {
        id: response.data.id,
        series_id: response.data.seriesId,
        task_id: 0, // 新接口没有 task_id，设为 0
        speaker_name: response.data.speakerName,
        description: response.data.description || '',
        is_series_speaker: response.data.isSeriesSpeaker || true,
        subtitles: response.data.subtitleRespVOS || []
      };
      
      return {
        success: true,
        message: response.msg || '创建说话人成功',
        data: speakerData
      };
    } else {
      throw new Error(response.msg || '创建说话人失败');
    }
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/voice-tone/add-series-speaker`,
      '请求参数': JSON.stringify(params),
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/video/voice-tone/add-series-speaker`,
      '请求方法': 'POST'
    };
    
    console.error('=== 创建说话人API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 重命名说话人（视频编辑页）
 * 接口：PUT /video/subtitles/rename-speaker
 * 请求参数：
 * {
 *   "speakerId": 1,
 *   "newName": "李四"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 * @param speakerId 说话人ID
 * @param newName 新名称
 * @returns Promise<{ code: number; data: boolean; msg: string }>
 */
export const renameVideoSpeakerApi = async (
  speakerId: number,
  newName: string
): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/rename-speaker`,
      {
        method: "PUT",
        body: { speakerId, newName }
      }
    );
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/subtitles/rename-speaker`,
      '说话人ID': speakerId,
      '新名称': newName,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/video/subtitles/rename-speaker`,
      '请求方法': 'PUT'
    };
    
    console.error('=== 重命名说话人API调用错误详情 ===');
    console.table(errorInfo); 
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 更新说话人信息（旧接口，保留用于其他场景）
 * @deprecated 在视频编辑页中请使用 renameVideoSpeakerApi
 * @param speakerId 说话人ID
 * @param params 更新说话人参数
 * @returns Promise<Speakers.UpdateSpeakerResponse>
 */
export const updateSpeakerApi = async (speakerId: number, params: Speakers.UpdateSpeakerParams): Promise<Speakers.UpdateSpeakerResponse> => {
  try {
    const response = await request<any>(
      `/speakers/${speakerId}`,
      {
        method: "PUT",
        body: params
      }
    );
    
    
    // 处理不同的响应格式
    let formattedResponse: Speakers.UpdateSpeakerResponse;
    
    // 情况1: 标准格式 {success: true, data: {...}}
    if (response && typeof response === 'object' && 'success' in response) {
      formattedResponse = response as Speakers.UpdateSpeakerResponse;
    }
    // 情况2: 直接返回说话人对象
    else if (response && typeof response === 'object' && 'id' in response && 'speaker_name' in response) {
      formattedResponse = {
        success: true,
        message: "更新说话人成功",
        data: response as Speakers.Speaker
      };
    }
    // 情况3: 其他格式
    else {
      formattedResponse = {
        success: false,
        message: "响应格式不正确",
        data: {} as Speakers.Speaker
      };
    }
    
    return formattedResponse;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/speakers/${speakerId}`,
      '说话人ID': speakerId,
      '请求参数': JSON.stringify(params),
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/speakers/${speakerId}`,
      '请求方法': 'PUT'
    };
    
    console.error('=== 更新说话人API调用错误详情 ===');
    console.table(errorInfo); 
    console.error('完整错误对象:', error);
    
    // 直接抛出错误，不再返回模拟数据
    throw error;
  }
};

/** 将 list-by-series 单条记录映射为 Speakers.Speaker */
function mapVoiceToneListBySeriesItem(item: any): Speakers.Speaker {
  return {
    id: item.id,
    series_id: item.seriesId,
    task_id: 0,
    speaker_name: item.speakerName,
    description: item.description || "",
    is_series_speaker: item.isSeriesSpeaker || false,
    subtitles: item.subtitleRespVOS || [],
  };
}

/**
 * 获取剧集说话人角色（GET `/video/voice-tone/list-by-series`）
 * @param seriesId 剧集 ID
 * @param _langCode 保留入参以兼容调用方；**不会写入 Query**，后端约定仅带 `langCode` 字段、不传语种值（空字符串）。
 */
export const getSeriesSpeakersApi = async (
  seriesId: number,
  _langCode?: string
): Promise<Speakers.GetSeriesSpeakersResponse | Speakers.Speaker[]> => {
  try {
    // 确保 seriesId 是数字类型
    const seriesIdNum = typeof seriesId === 'number' ? seriesId : Number(seriesId);
    
    if (isNaN(seriesIdNum)) {
      throw new Error('seriesId 必须是有效的数字');
    }

    // Query：带 langCode 键、值为空（不传具体语种）
    const params = { seriesId: seriesIdNum, langCode: "" };

    const response = await request<any>(
      `/video/voice-tone/list-by-series`,
      {
        method: "GET",
        params: params
      }
    );

    // 处理响应数据格式转换
    // 新格式：{ code: 0, data: [{ id, speakerName, isSeriesSpeaker, description, seriesId, subtitleRespVOS }] }
    // 旧格式：{ success: true, data: [{ id, speaker_name, is_series_speaker, description, series_id, subtitles }] }
    if (response && response.code === 0 && Array.isArray(response.data)) {
      const speakers: Speakers.Speaker[] = response.data.map(mapVoiceToneListBySeriesItem);

      return {
        success: true,
        message: '获取说话人列表成功',
        data: speakers
      };
    }

    // 如果响应格式不符合预期，返回空数组
    return {
      success: false,
      message: '响应格式不正确',
      data: []
    };
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/video/voice-tone/list-by-series',
      '剧集ID': seriesId,
      '语言代码': '(Query 中 langCode 固定为空)',
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求方法': 'GET'
    };
    
    console.error('=== 获取剧集说话人角色API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 工作台音色库单层：按剧集拉取说话人（GET 同上，`langCode` Query 为空字符串）。
 * 与 {@link getSeriesSpeakersApi} 行为一致；保留独立封装便于语义区分。
 */
export const getSeriesSpeakersAllLangsApi = async (
  seriesId: number
): Promise<Speakers.GetSeriesSpeakersResponse> => {
  const seriesIdNum = typeof seriesId === "number" ? seriesId : Number(seriesId);
  if (isNaN(seriesIdNum)) {
    throw new Error("seriesId 必须是有效的数字");
  }

  const response = await request<any>(`/video/voice-tone/list-by-series`, {
    method: "GET",
    params: { seriesId: seriesIdNum, langCode: "" },
  });

  if (response && response.code === 0 && Array.isArray(response.data)) {
    const speakers: Speakers.Speaker[] = response.data.map(mapVoiceToneListBySeriesItem);
    return {
      success: true,
      message: "获取说话人列表成功",
      data: speakers,
    };
  }

  return {
    success: false,
    message: "响应格式不正确",
    data: [],
  };
};

/**
 * 获取音色库剧集语言列表
 * GET /video/voice-tone/query-series-lang-list
 * @param seriesId 剧集ID
 * @returns 剧集语言列表（包含每个语言的说话人数量）
 */
export const getSpeakersSeriesApi = async (seriesId: number): Promise<Speakers.SeriesLangListResponse> => {
  // 确保 seriesId 是数字类型
  const seriesIdNum = typeof seriesId === 'number' ? seriesId : Number(seriesId);
  
  if (isNaN(seriesIdNum)) {
    throw new Error('seriesId 必须是有效的数字');
  }
  
  const response = await request<Speakers.SeriesLangListResponse>(
    `/video/voice-tone/query-series-lang-list`,
    {
      method: "GET",
      params: { seriesId: seriesIdNum }
    }
  );
  return response;
};

/**
 * 查询音色库内层字幕信息
 * GET /video/voice-tone/query-by-role-and-language
 * Query 参数：speakerId, langCode
 * 响应数据：
 * {
 *   code: 0,
 *   data: [
 *     {
 *       id: 0,
 *       originText: "",
 *       score: 0.0,
 *       originAudio: "",
 *       originStartTime: "",
 *       originEndTime: ""
 *     }
 *   ],
 *   msg: ""
 * }
 */
export const queryVoiceToneByRoleAndLanguageApi = async (
  speakerId: number,
  langCode: string
): Promise<{
  code: number;
  data: Array<{
    id: number;
    originText: string;
    score: number;
    originAudio: string;
    originStartTime: string;
    originEndTime: string;
  }>;
  msg: string;
}> => {
  try {
    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        originText: string;
        score: number;
        originAudio: string;
        originStartTime: string;
        originEndTime: string;
      }>;
      msg: string;
    }>(
      `/video/voice-tone/query-by-role-and-language`,
      {
        method: "GET",
        params: {
          speakerId,
          langCode
        }
      }
    );

    return response;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': '/video/voice-tone/query-by-role-and-language',
      '说话人ID': speakerId,
      '语言代码': langCode,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求方法': 'GET'
    };

    console.error('=== 查询音色库内层字幕信息API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);

    throw error;
  }
};

/**
 * 工作台音色库：按说话人查片段，query 仅带 speakerId，langCode 传键不传值（…&langCode）。
 * 与 queryVoiceToneByRoleAndLanguageApi(speakerId, "") 不同，避免被序列化为 langCode=。
 */
export const queryVoiceToneBySpeakerWorkspaceApi = async (
  speakerId: number
): Promise<{
  code: number;
  data: Array<{
    id: number;
    originText: string;
    score: number;
    originAudio: string;
    originStartTime: string;
    originEndTime: string;
  }>;
  msg: string;
}> => {
  const sid = typeof speakerId === "number" ? speakerId : Number(speakerId);
  if (Number.isNaN(sid)) {
    throw new Error("speakerId 必须是有效的数字");
  }
  const path = `/video/voice-tone/query-by-role-and-language?speakerId=${encodeURIComponent(
    String(sid)
  )}&langCode`;
  try {
    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        originText: string;
        score: number;
        originAudio: string;
        originStartTime: string;
        originEndTime: string;
      }>;
      msg: string;
    }>(path, { method: "GET" });

    return response;
  } catch (error: any) {
    const errorInfo = {
      "API调用失败": "/video/voice-tone/query-by-role-and-language",
      说话人ID: sid,
      查询串: "speakerId + langCode(无值)",
      状态码: error?.status || "N/A",
      状态文本: error?.statusText || "N/A",
      服务器响应: error?.response || "N/A",
      错误消息: error?.message || "N/A",
      请求方法: "GET",
    };
    console.error("=== 查询音色库内层字幕信息（工作台）API调用错误详情 ===");
    console.table(errorInfo);
    console.error("完整错误对象:", error);
    throw error;
  }
};

/**
 * 将字幕添加到剧集音色库
 * 新接口：PUT /video/subtitles/add-to-voice-library
 * 请求参数：
 * {
 *   "subtitleId": 123,
 *   "speakerId": 456
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const promoteSpeakerApi = async (
  speakerId: number,
  subtitleId: number
): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/add-to-voice-library`,
      {
        method: "PUT",
        body: {
          subtitleId,
          speakerId
        }
      }
    );

    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * 从音色库中移除字幕（音色库音频）
 * 新接口：PUT /video/voice-tone/remove-by-id
 * 请求参数：
 * {
 *   "id": 123 // 台词id
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const demoteSpeakerSubtitleApi = async (
  subtitleId: number
): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/voice-tone/remove-by-id`,
    {
      method: "PUT",
      params: {
        id: subtitleId
      }
    }
  );
  return response;
};

/**
 * 删除说话人
 * DELETE /speakers/{speakerId}
 */
export const deleteSpeakerApi = async (speakerId: number): Promise<{ success?: boolean; message?: string } | void> => {
  const response = await request<{ success?: boolean; message?: string }>(
    `/video/voice-tone/remove-series-speaker`,
    {
      method: "DELETE",
      params: {
        speakerId
      }
    }
  );
  return response;
};



